import React, { useEffect, ReactNode, useCallback } from 'react';
import { useIntl } from 'react-intl';
import classNames from 'classnames';
import { CentPrecisionMoney } from '@commercetools/platform-sdk';

import styles from './AddToCartSimplifiedView.module.scss';
import messages from './messages';

import PrimaryButton from '../../common/PrimaryButton';

import { priceToString } from '../../../utils';
import AffirmAsLowAs from '../../AffirmAsLowAs';
import useDrupalSettings from '../../../hooks/useDrupalSettings';
import PromoBannerTracker from '../../../blocks/PromoBannerTracker';
import TruemedWidget from '../../TruemedWidget';

const AddToCartSimplifiedView = ({
  addToCartButtonTitle = '',
  children = undefined,
  hasAddLineItemsError = false,
  handleAddToCart,
  isAddToCartButtonLoading = false,
  showTotals = false,
  total = {
    centAmount: 0,
    currencyCode: 'USD',
    fractionDigits: 2,
    type: 'centPrecision',
  },
  totalDiscount = undefined,
  productTitle = '',
  selectedSku = '',
}: {
  addToCartButtonTitle: string;
  children?: ReactNode;
  hasAddLineItemsError: boolean;
  handleAddToCart: React.MouseEventHandler<HTMLButtonElement>;
  isAddToCartButtonLoading: boolean;
  showTotals: boolean;
  total?: CentPrecisionMoney;
  totalDiscount?: CentPrecisionMoney;
  productTitle: string;
  selectedSku: string;
}) => {
  const { formatMessage, formatNumber } = useIntl();

  const { featureToggle, truemed } = useDrupalSettings() as {
    featureToggle: string[];
    promotionalBanner: {
      tiers: { discount: string }[];
    };
    truemed: {
      skus: string[];
    };
  };

  const isTruemedEligible = useCallback(() => {
    if (truemed?.skus?.includes(selectedSku)) {
      return true;
    }
    return false;
  }, [truemed, selectedSku]);

  const showTruemedWidget = window?.purTogglesEnabled('truemed_payment');

  const showStickyCart = featureToggle?.includes('sticky_atc');

  /**
   * Sticky add to cart header.
   */
  let lastScrollY = window.scrollY;
  const isSticky = () => {
    const stickySection = document.querySelector('#add-to-cart-section');
    const stickyCartWrapper = document.querySelector(
      '.sticky-add-to-cart-wrapper',
    );
    const stickyCart = document.querySelector(
      '#add-to-cart-section .sticky-add-to-cart',
    );

    if (stickyCart && stickySection && stickyCartWrapper) {
      // Look at the regular add to cart button, and if it goes off the screen,
      // activate the sticky one.
      const rect = stickySection.getBoundingClientRect();
      const offset = window.innerWidth < 1000 ? window.innerHeight * -0.1 : 0;
      if (rect.top < offset) {
        stickyCart.classList.add(styles['is-sticky']);
        if (window.scrollY < lastScrollY) {
          // Scrolling up
          stickyCartWrapper.classList.add(styles['scroll-up']);
        } else {
          // Scrolling down
          stickyCartWrapper.classList.remove(styles['scroll-up']);
        }
      } else {
        stickyCart.classList.remove(styles['is-sticky']);
        stickyCartWrapper.classList.remove(styles['scroll-up']);
      }
      lastScrollY = window.scrollY;
    }
  };

  useEffect(() => {
    isSticky();
    window.addEventListener('scroll', isSticky);
    return () => {
      window.removeEventListener('scroll', isSticky);
    };
  });

  return (
    <div className={styles.container}>
      {showTotals && (
        <>
          <div className={styles['pricing-and-financing-container']}>
            <div className={styles['pricing-total']}>
              <div className={styles['pricing-total-legend-container']}>
                <div>Total</div><span> </span><div>Price</div>
              </div>
              <div
                className={classNames(
                  styles['pricing-info'],
                  styles['pricing-info-horizontal'],
                )}
              >
                <div
                  className={classNames(styles['product-price'], {
                    [styles['product-price-line-through']]:
                      (totalDiscount?.centAmount ?? 0) > 0,
                  })}
                >
                  {priceToString({
                    price: {
                      ...total,
                      centAmount:
                        total.centAmount + (totalDiscount?.centAmount ?? 0),
                    },
                    forceDigit: false,
                    maximumFractionDigits: 0,
                    formatNumber,
                    formatMessage,
                  })}
                </div>
                {(totalDiscount?.centAmount ?? 0) > 0 && (
                  <div className={styles['product-discounted-price']}>
                    {priceToString({
                      price: total,
                      maximumFractionDigits: 0,
                      forceDigit: false,
                      formatNumber,
                      formatMessage,
                    })}
                  </div>
                )}
              </div>
              <div className={styles['pricing-subtotal-finance-separator']}>
                <div
                  className={styles['pricing-subtotal-finance-separator-bar']}
                />
                {formatMessage(messages.priceOrFinancing)}
                <div
                  className={styles['pricing-subtotal-finance-separator-bar']}
                />
              </div>
              <div className={styles['pricing-subtotal-finance-affirm-container']}>
                <div className={styles['pricing-subtotal-finance-affirm']}>
                  <span className={styles['pricing-subtotal-finance-amount']}>
                    <div>
                      <span>{formatMessage(messages.affirmAsLowAs)} </span>
                      <AffirmAsLowAs
                        placement='cart'
                        total={total?.centAmount}
                      />
                    </div>
                  </span>
                </div>
                <div className={styles['pricing-subtotal-finance-affirm-2nd-line']}>
                  {formatMessage(messages.affirmAPRText)}
                </div>
              </div>
            </div>
          </div>
          <PromoBannerTracker hasEstimatedValue />
          <div className={styles['add-to-cart-button-container']}>
            <PrimaryButton
              onClick={handleAddToCart}
              isLoading={isAddToCartButtonLoading}
              title={addToCartButtonTitle}
              size='large'
            />
            {showStickyCart && (
              <div
                className={classNames(
                  'sticky-add-to-cart-wrapper',
                  styles['sticky-add-to-cart-wrapper'],
                )}
              >
                <div
                  className={classNames(
                    'sticky-add-to-cart',
                    styles['sticky-add-to-cart'],
                  )}
                >
                  <div className={styles['sticky-title-price']}>
                    <div className={styles['sticky-title']}>{productTitle}</div>
                    <div className={styles['sticky-price']}>
                      <span
                        className={classNames({
                          [styles['product-price-line-through']]:
                            (totalDiscount?.centAmount ?? 0) > 0,
                        })}
                      >
                        {total
                          ? priceToString({
                              price: {
                                ...total,
                                centAmount:
                                  total.centAmount +
                                  (totalDiscount?.centAmount ?? 0),
                              },
                              maximumFractionDigits: 0,
                              forceDigit: false,
                              formatNumber,
                              formatMessage,
                            })
                          : ''}
                      </span>
                      {(totalDiscount?.centAmount ?? 0) > 0 && (
                        <span className={styles['product-discounted-price']}>
                          {priceToString({
                            price: total,
                            maximumFractionDigits: 0,
                            forceDigit: false,
                            formatNumber,
                            formatMessage,
                          })}
                        </span>
                      )}
                    </div>
                  </div>
                  <div>
                    <PrimaryButton
                      onClick={handleAddToCart}
                      isLoading={isAddToCartButtonLoading}
                      title={addToCartButtonTitle}
                      className={styles['sticky-button']}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
          {hasAddLineItemsError && (
            <div className={styles['add-error']}>
              {formatMessage(messages.errorAddingItems)}
            </div>
          )}
          {children}
          <AffirmAsLowAs placement='product' total={total?.centAmount} />
          {showTruemedWidget && isTruemedEligible() && <TruemedWidget />}
        </>
      )}
    </div>
  );
};

export default AddToCartSimplifiedView;
